import { toRaw } from 'vue'

import type { Offer, Pace, Period } from '@brighterly/lib-core-types/plans/interfaces'
import type { PadStore } from './interfaces'

import { useDisciplineStore } from '@stores/discipline'

const getMathPaces = (state: PadStore): Pace[] => state.plansMath?.paces || []
const getElaPaces = (state: PadStore): Pace[] => state.plansEla?.paces || []

const getPace = (state: PadStore): Pace['pace'] | null => {
  const disciplineStore = useDisciplineStore()
  return disciplineStore.isEla
    ? state.paceEla
    : disciplineStore.isMath
      ? state.paceMath
      : null
}

const getPaces = (state: PadStore): Pace[] => {
  const disciplineStore = useDisciplineStore()
  const paces = disciplineStore.isEla
    ? getElaPaces(state)
    : disciplineStore.isMath
      ? getMathPaces(state)
      : []
  return paces.map(pace => {
    const periods = pace.periods.filter(period => period?.product?.options?.premium_tutor === false)
    return {
      ...toRaw(pace),
      periods: toRaw(periods),
    }
  })
}

const getOffer = (state: PadStore): Offer | null => {
  const elaOffer = state.plansEla?.offer || null
  const mathOffer = state.plansMath?.offer || null
  const disciplineStore = useDisciplineStore()
  if (disciplineStore.isEla) {
    return elaOffer
  } else if (disciplineStore.isMath) {
    return mathOffer
  } else {
    return null
  }
}

const isPricesLoading = (state: PadStore): boolean => {
  const disciplineStore = useDisciplineStore()
  const request = disciplineStore.isEla
    ? state.requestEla
    : disciplineStore.isMath
      ? state.requestMath
      : null

  return request?.status === 'loading'
}

const getOfferDeadline = (state: PadStore): Offer['deadline'] | null => {
  const offer = getOffer(state)
  return offer?.deadline || null
}

export const getters = {
  hasPaces: (state: PadStore): boolean => {
    const disciplineStore = useDisciplineStore()
    if (disciplineStore.isEla) {
      return !!state.plansEla?.paces
    } else if (disciplineStore.isMath) {
      return !!state.plansMath?.paces
    } else {
      return false
    }
  },
  isLoading: (state: PadStore): PadStore['loading'] => state.loading,
  isPricesLoading: (state: PadStore): boolean => isPricesLoading(state),
  getMathOffer: (state: PadStore): Offer | null => state.plansMath?.offer || null,
  getElaOffer: (state: PadStore): Offer | null => state.plansEla?.offer || null,
  getOffer: (state: PadStore): Offer | null => getOffer(state),
  getOfferDeadline: (state: PadStore): Offer['deadline'] | null => getOfferDeadline(state),
  getPaces: (state: PadStore): Pace[] => getPaces(state),
  getPace: (state: PadStore): Pace['pace'] | null => getPace(state),
  getPeriods (state: PadStore): Period[] {
    const pace = getPace(state)

    if (!pace) return []

    const selectedPace = getPaces(state).find(item => item.pace === pace)
    return selectedPace?.periods ?? []
  },
  getPeriodIndex: (state: PadStore): number | null => state.periodIndex,
  isPeriodIndex: (state: PadStore): boolean => (typeof state.periodIndex === 'number'),
  isCountdownExpired: (state: PadStore): PadStore['countdownExpired'] => state.countdownExpired,
  isModalFaq: (state: PadStore): PadStore['modalFaq'] => state.modalFaq,
}
